import React, { useState, useRef, ChangeEvent, ClipboardEvent } from 'react';
import styled from 'styled-components';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import Spinner from './Spinner';
import throttle from 'lodash.throttle';
import { useTranslation } from '~/Locale';
import {
  BodyText,
  CheckCircleIcon,
  Message,
  Stack,
  Title
} from '@kvdbil/components';
import { verifyPhoneNumber } from '~/App/shared/actions/verifyPhoneNumber';
import { useDispatch, useSelector } from 'react-redux';
import {
  phoneVerificationError,
  phoneVerificationIsMaxRetriesExceeded,
  phoneVerificationIsVerified,
  phoneVerificationIsVerifying
} from '~/App/shared/selectors/verifyPhoneNumberSelector';
import { phoneNumber as phoneNumberSelector } from '~/App/shared/selectors/sessionSelector';
import VerifyFooter from './VerifyFooter';

const Container = styled(Stack)`
  width: 100%;
  text-align: center;
`;

const InputContainer = styled.div`
  width: 100%;
  display: flex;
  overflow: hidden;
  gap: 0.5rem;
  justify-content: center;

  input:nth-child(4) {
    margin-right: 1rem;
  }
`;

const InputBox = styled.input`
  outline: none;
  width: 2rem;
  height: 2.5rem;
  font-size: 1.5rem;
  font-weight: 500;
  text-align: center;
  border-radius: 0.25rem;
  color: ${({ theme }) => theme.colors.text.dark};
  border: 2px solid ${({ theme }) => theme.colors.grayscaleToned.dark3};

  ${({ disabled, theme }) =>
    disabled &&
    `
      border: 2px solid ${theme.colors.grayscaleToned.light3};
      background-color: ${theme.colors.background.gray};
    `}
`;

const AnimationContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  height: 10rem;

  .verifyInput-enter {
    max-width: 3.5rem;
    opacity: 0;
  }

  .verifyInput-enter.verifyInput-enter-active {
    max-width: 18rem;
    opacity: 1;
    transition:
      max-width 0.3s ease-in-out 0.2s,
      opacity 0.2s ease-in-out;
  }

  .verifyInput-exit {
    opacity: 1;
    width: 100%;
    max-width: 18rem;
  }

  .verifyInput-exit.verifyInput-exit-active {
    opacity: 0;
    max-width: 3.5rem;
    overflow: hidden;
    transition:
      max-width 0.3s ease-in-out,
      opacity 0.2s ease-in-out 0.3s;
  }
`;

const TransitionWrapper = styled.div`
  display: flex;
  justify-content: center;
  overflow: hidden;
`;

const VerifiedStatus = styled.div`
  color: ${({ theme }) => theme.colors.secondary.main};

  svg {
    font-size: 6rem;
  }
`;

const transitionProps = {
  classNames: 'verifyInput',
  timeout: {
    enter: 500,
    exit: 500
  }
};

const CODE_LENGTH = 8;

export type CodeVerification = { code: string; delay: number };

interface Props {
  handleClose?: () => void;
}

const EnterCode = ({ handleClose }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isVerifying = useSelector(phoneVerificationIsVerifying);
  const isMaxRetriesExceeded = useSelector(
    phoneVerificationIsMaxRetriesExceeded
  );
  const error = useSelector(phoneVerificationError);
  const phoneNumber = useSelector(phoneNumberSelector);
  const isVerified = useSelector(phoneVerificationIsVerified);
  const inputs = Array.from({ length: CODE_LENGTH });
  const [inputsState, setInputsState] = useState<string[]>([]);

  const clearInputs = () => {
    inputRefs.current.forEach(el => {
      if (el) el.value = '';
    });
    setInputsState([]);
  };

  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const transitionRef = useRef<HTMLDivElement>(null);

  const isInputDisabled = isVerifying || isMaxRetriesExceeded;

  const throttledVerifyCode = throttle((code: string) => {
    /* Delay until actual request is sent. So we don't ruin animation :) */
    dispatch(verifyPhoneNumber({ code, delay: 750 }));
    clearInputs();
  }, 250);

  const handleChange = (
    event: ChangeEvent<HTMLInputElement>,
    currentIndex: number
  ) => {
    const { value } = event.target;

    const isValid = value.length < 2;
    const isCodeValid = inputsState.join('').length === CODE_LENGTH - 1;
    const shouldFocus = value.length === 1;

    if (!isValid) {
      return;
    }

    setInputsState(prevValue => {
      if (prevValue.length === 0) {
        return [value];
      } else {
        const newArray = [...prevValue];
        newArray.splice(currentIndex, 1, value);

        return newArray;
      }
    });
    shouldFocus && inputRefs?.current?.[currentIndex + 1]?.focus();

    if (currentIndex + 1 === CODE_LENGTH && isCodeValid) {
      const codeToVerify = inputsState.join('');
      throttledVerifyCode(`${codeToVerify}${value}`);
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    clearInputs();

    const pastedText = event.clipboardData
      ?.getData('text')
      .replace(/\D/g, '')
      .slice(0, CODE_LENGTH);
    const pastedTextArr = pastedText.split('');
    setInputsState(pastedTextArr);

    pastedTextArr.forEach((number, index) => {
      const input = inputRefs?.current?.[index];

      if (input) {
        input.value = number;
        if (pastedText.length === index + 1) {
          inputRefs.current[index + 1]?.focus();
        }
      }
    });

    setTimeout(() => {
      if (pastedText.length === CODE_LENGTH) {
        inputRefs?.current?.[CODE_LENGTH]?.focus();
        throttledVerifyCode(pastedText);
      }
    }, 500);
  };

  const hasError = Boolean(error && !isVerifying);

  return (
    <Container rowGap={2}>
      <Stack>
        <Title as="h3">{t('Verify phone number')}</Title>
        <BodyText>{t('We need to confirm that your number ...')}</BodyText>
      </Stack>
      <BodyText>
        {`${t('Type the eight character long code we have sent ...')} `}
        <strong>{phoneNumber}</strong>
      </BodyText>

      {hasError && (
        <Message type="error" fullWidth>
          {error || t('Something went wrong, please try again later.')}
        </Message>
      )}

      <AnimationContainer>
        <SwitchTransition>
          <CSSTransition
            {...transitionProps}
            nodeRef={transitionRef}
            key={!isVerifying && !isVerified ? 'showInput' : 'showSpinner'}
          >
            <TransitionWrapper ref={transitionRef}>
              {!isVerifying && !isVerified && (
                <InputContainer key={1}>
                  {inputs.map((_, index) => {
                    return (
                      <InputBox
                        autoFocus={index === 0}
                        key={index + 1}
                        type="number"
                        disabled={isInputDisabled}
                        ref={el => (inputRefs.current[index] = el)}
                        onChange={e => handleChange(e, index)}
                        onPaste={handlePaste}
                        data-val={index + 1}
                      />
                    );
                  })}
                </InputContainer>
              )}
              {isVerifying && !isVerified && (
                <Spinner isLoading={isVerifying} />
              )}
              {isVerified && (
                <VerifiedStatus>
                  <CheckCircleIcon />
                </VerifiedStatus>
              )}
            </TransitionWrapper>
          </CSSTransition>
        </SwitchTransition>
      </AnimationContainer>

      <VerifyFooter handleClose={handleClose} />
    </Container>
  );
};

export default EnterCode;
